import { ProductTypeQueryParam } from "application/store/models/seleccionarProductoPaso";
import { saveAutthorization } from "application/store/reducers/EnrollmentAutthorizationReducer";
import { saveBiometricEnrollmentUser } from "application/store/reducers/EnrollmentUserReducer";
import { APPLYING_BIOMETRIC } from "application/utils/constants";
import { getEnrollmentStatusByUserIdService } from "infrastructure/services/get-enrollment-status-by-user-id.services";
import { postEnrollmentAuthorizationServices } from "infrastructure/services/post-enrollment-authorization.services";
import { MixpanelService } from "infrastructure/services/tracker/mixpanel.service";
import { BIOMETRIC_ENROLMENT_STATUS } from "infrastructure/services/types/enrollments.entity";
import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";

type EnrollmentStatusResult =
  | { success: true }
  | { success: false; reason?: "userBlocked" };

export function useBiometricEnrollmentProcess() {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getUserId = ({
    documentCountry,
    documentNumber,
    documentType,
  }: {
    documentNumber: string;
    documentCountry: string;
    documentType: string;
  }) => {
    if (
      documentCountry &&
      documentCountry === "858" &&
      documentNumber &&
      documentType &&
      documentType === "CI"
    ) {
      const userId = `${documentCountry}-1-${documentNumber
        .replaceAll(".", "")
        .replace("-", "")}`;
      dispatch(saveAutthorization({ userId }));
      return userId;
    }
    return "";
  };

  const getBiometricEnrollmentStatusByUserId = useCallback(
    async ({
      captchaToken,
      documentCountry,
      documentNumber,
      documentType,
      productType,
    }: {
      documentNumber: string;
      documentCountry: string;
      documentType: string;
      captchaToken: string;
      productType: ProductTypeQueryParam;
    }): Promise<EnrollmentStatusResult> => {
      let userId = getUserId({ documentCountry, documentNumber, documentType });

      if (
        APPLYING_BIOMETRIC &&
        // !isPymes &&
        // ![ProductTypeQueryParam.PymesParam].includes(productType) &&
        userId
      ) {
        setIsLoading(true);

        MixpanelService.track(
          `Start: get enrollments by userId`,
          {
            userId,
          },
          true,
        );

        try {
          const { status, data } = await getEnrollmentStatusByUserIdService({
            userId,
          });
          if ("enrollmentId" in data) {
            MixpanelService.track(
              `${data.status} Status: get enrollments by userId`,
              {
                userId,
              },
              true,
            );

            if (
              status === 200 &&
              data.status === BIOMETRIC_ENROLMENT_STATUS.COMPLETED
            ) {
              dispatch(saveBiometricEnrollmentUser(data));
              return { success: true };
            }
          } else if (status === 423) {
            MixpanelService.track(
              `Error: unhandled error ${data.errors?.[0]?.code} - ${data.errors?.[0].description}`,
              {
                userId,
              },
              true,
            );
            return { success: false, reason: "userBlocked" };
          }

          if (status === 404 || status === 200) {
            const res = await postEnrollmentAuthorizationServices({
              userId,
              captchaToken,
            });

            if ("enrollmentId" in res.data) {
              MixpanelService.track(
                `${res.data.status} Status: post enrollments authorization service`,
                {
                  userId,
                },
                true,
              );

              if (
                res.status === 200 &&
                res.data.status === BIOMETRIC_ENROLMENT_STATUS.AUTHORIZED
              ) {
                dispatch(saveBiometricEnrollmentUser(res.data));
                return { success: true };
              } else if (
                res.data.status === BIOMETRIC_ENROLMENT_STATUS.BLOCKED ||
                res.data.status === BIOMETRIC_ENROLMENT_STATUS.UNAUTHORIZED
              ) {
                return { success: false };
              }
            }
            return { success: false };
          }

          return { success: false };
        } catch (error) {
          console.error("Error fetching biometric enrollment status:", error);

          MixpanelService.track(
            `Errors Status: get enrollments by userId`,
            {
              userId,
            },
            true,
          );

          return { success: false };
        } finally {
          setIsLoading(false);
        }
      } else {
        return { success: true };
      }
    },
    [dispatch],
  );

  return {
    getBiometricEnrollmentStatusByUserId,
    isLoading,
  };
}
